// src/pages/AmbassadorsPage.tsx
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Globe, Heart } from "lucide-react";

import { useMouseParallax } from "../hooks/useMouseParallax";
import { EARNINGS_EXAMPLES } from "../types/ambassador.types";

import { BackgroundGlows } from "../components/ambassadors/BackgroundGlows";
import { FloatingParticles } from "../components/ambassadors/FloatingParticles";
import { StatCard } from "../components/ambassadors/StatCard";
import { EarningsCard } from "../components/ambassadors/EarningsCard";
import { RegistrationForm } from "../components/ambassadors/RegistrationForm";
import { SuccessModal } from "../components/ambassadors/SuccessModal";
import { StepCard } from "../components/ambassadors/StepCard";
import { useNavigate } from "react-router-dom";

const COMMISSION_PER_PURCHASE = 40;
const MINIMUM_PAYOUT = 400;

export default function AmbassadorsPage() {
  const [isMobile, setIsMobile] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const transforms = useMouseParallax(isMobile);
  const navigate = useNavigate();

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return (
    <div className="relative min-h-screen bg-[#040508] text-white overflow-x-hidden">
      <BackgroundGlows
        goldX={transforms.gold.x}
        goldY={transforms.gold.y}
        roseX={transforms.rose.x}
        roseY={transforms.rose.y}
        blueX={transforms.blue.x}
        blueY={transforms.blue.y}
        isMobile={isMobile}
      />

      <FloatingParticles />

      <div className="relative z-10">
        <section className="min-h-screen flex items-center justify-center px-4 pt-24 pb-16">
          <div className="max-w-6xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/20 rounded-full mb-8">
                <span className="text-[9px] md:text-[10px] uppercase tracking-wider text-amber-400">
                  LRNSY Ambassador Program
                </span>
              </div>

              <h1 className="text-4xl md:text-6xl lg:text-[80px] font-medium tracking-tighter uppercase leading-[0.85] mb-6">
                Represent LRNSY.
                <br />
                <span
                  className="text-transparent"
                  style={{ WebkitTextStroke: "1px rgba(255,255,255,0.8)" }}
                >
                  Earn Through Meaningful Influence
                </span>
              </h1>

              <p className="text-sm md:text-base lg:text-lg text-white/65 max-w-3xl mx-auto mb-10 leading-relaxed">
                LRNSY helps people express love, care, appreciation, forgiveness, and connection
                more intentionally through meaningful experiences. We’re inviting a small group of
                creators to help introduce LRNSY to the people and communities they influence.
              </p>

              <div className="max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-4 mb-14">
                <StatCard value={`KES ${COMMISSION_PER_PURCHASE}`} label="per qualified purchase" />
                <StatCard value="Shortlisted" label="after application review" />
                <StatCard value={`KES ${MINIMUM_PAYOUT}`} label="minimum payout" />
              </div>

              <motion.a
                href="#apply"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="group inline-flex items-center gap-4 px-8 py-4 md:px-10 md:py-5 bg-white text-black rounded-full font-bold uppercase tracking-tighter text-xs md:text-sm shadow-[0_20px_40px_rgba(0,0,0,0.4)] hover:scale-105 transition-all"
              >
                <Heart
                  size={16}
                  className="fill-black group-hover:scale-110 transition-transform"
                />
                Apply to Become an Ambassador
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </motion.a>
            </motion.div>
          </div>
        </section>

        <section className="py-4 md:py-8 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-4">
                How It{" "}
                <span
                  className="text-transparent"
                  style={{ WebkitTextStroke: "1px rgba(255,255,255,0.8)" }}
                >
                  Works
                </span>
              </h2>
              <p className="text-white/45 text-sm max-w-2xl mx-auto">
                A curated process designed to select ambassadors who genuinely fit LRNSY.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <StepCard
                number="01"
                title="Apply"
                description="Tell us about your audience, your content, and why you feel aligned with LRNSY."
              />
              <StepCard
                number="02"
                title="Get Reviewed"
                description="We shortlist the strongest fits and invite selected applicants to a short Zoom conversation."
              />
              <StepCard
                number="03"
                title="Get Activated"
                description="Approved ambassadors receive onboarding, a personal page, tracking, content direction, and commission access."
              />
            </div>
          </div>
        </section>

        <section className="px-4 bg-gradient-to-b from-transparent via-amber-500/5 to-transparent">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-4">
                Example{" "}
                <span
                  className="text-transparent"
                  style={{ WebkitTextStroke: "1px rgba(255,255,255,0.8)" }}
                >
                  Earnings
                </span>
              </h2>
              <p className="text-white/45 text-sm max-w-2xl mx-auto">
                A simple view of how earnings can grow over time.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {EARNINGS_EXAMPLES.map((example, index) => (
                <EarningsCard
                  key={index}
                  purchases={example.purchases}
                  earnings={example.earnings}
                />
              ))}
            </div>

            <div className="mt-10 grid md:grid-cols-4 gap-4 text-left">
              {[
                "Personal ambassador page",
                "Tracked clicks and purchases",
                "Commission on qualified purchases",
                "Performance-based rewards over time",
              ].map((item) => (
                <div
                  key={item}
                  className="bg-white/5 border border-white/10 rounded-2xl p-5 flex items-start gap-3"
                >
                  <CheckCircle2 size={18} className="text-amber-400 mt-0.5" />
                  <p className="text-sm text-white/70">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="apply" className="py-16 md:py-24 px-4">
          <RegistrationForm onSuccess={() => setShowSuccess(true)} />
        </section>

        <section className="py-12 md:py-16 px-4 border-t border-white/10">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-xl md:text-2xl lg:text-3xl font-serif italic text-white/80 mb-6">
              “The strongest creators don’t just sell attention. They shape how people feel.”
            </h3>
            <div className="flex items-center justify-center gap-4 text-white/20">
              <Globe size={14} />
              <span className="text-[9px] md:text-[10px] uppercase tracking-wider">
                Apply to become part of LRNSY’s ambassador community
              </span>
            </div>
          </div>
        </section>
      </div>

      {showSuccess && (
        <SuccessModal
          onClose={() => {
            setShowSuccess(false);
            navigate("/");
          }}
        />
      )}
    </div>
  );
}
